import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { FileUpload } from "@/components/admin/FileUpload";

/**
 * Generic CRUD manager used by admin sub-pages.
 * fields: [{ name, label, type: "text"|"textarea"|"number"|"select"|"checkbox"|"url", options?, placeholder? }]
 */
export function CrudManager({ title, description, endpoint, fields, emptyValues, renderCell, columns, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyValues);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => { setEditing(null); setForm(emptyValues); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyValues, ...it }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); setForm(emptyValues); };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // Convert list-like string values
      fields.forEach((f) => {
        if (f.type === "list" && typeof payload[f.name] === "string") {
          payload[f.name] = payload[f.name].split("\n").map((s) => s.trim()).filter(Boolean);
        }
        if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0;
      });

      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created");
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name || it.title}"?`)) return;
    try { await api.delete(`${endpoint}/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900">{title}</h1>
          <p className="text-slate-500 mt-1">{description}</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid={`${testidPrefix}-new`}>
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      <div className="mt-8 card-medi overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                {columns.map((c) => <th key={c.key} className="text-left px-6 py-3 font-medium">{c.label}</th>)}
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={columns.length + 1} className="px-6 py-6 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-6 py-6 text-slate-500">No items yet.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} data-testid={`${testidPrefix}-row-${it.id}`}>
                  {columns.map((c) => <td key={c.key} className="px-6 py-4 align-middle">{renderCell ? renderCell(c.key, it) : it[c.key]}</td>)}
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(it)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`${testidPrefix}-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(it)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`${testidPrefix}-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4 overflow-y-auto" data-testid={`${testidPrefix}-modal`}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              <button onClick={close} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`${testidPrefix}-close`}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {fields.map((f) => (
                <FormField key={f.name} field={f} value={form[f.name]} onChange={(v) => setField(f.name, v)} testid={`${testidPrefix}-field-${f.name}`} />
              ))}
              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={close} className="px-5 py-2.5 rounded-full text-slate-600 hover:bg-slate-100">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid={`${testidPrefix}-save`}>
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ field, value, onChange, testid }) {
  const base = "mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500";
  const displayValue = value ?? (field.type === "checkbox" ? false : "");

  if (field.type === "textarea") {
    return (
      <div>
        <label className="text-sm font-medium text-slate-700">{field.label}</label>
        <textarea rows={field.rows || 4} value={displayValue} onChange={(e) => onChange(e.target.value)} className={base} placeholder={field.placeholder} data-testid={testid} />
      </div>
    );
  }
  if (field.type === "list") {
    const asText = Array.isArray(displayValue) ? displayValue.join("\n") : displayValue;
    return (
      <div>
        <label className="text-sm font-medium text-slate-700">{field.label} <span className="text-xs text-slate-400">(one per line)</span></label>
        <textarea rows={field.rows || 4} value={asText} onChange={(e) => onChange(e.target.value)} className={base} placeholder={field.placeholder} data-testid={testid} />
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!displayValue} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="rounded border-slate-300 text-red-600 focus:ring-red-500" />
        {field.label}
      </label>
    );
  }
  if (field.type === "url") {
    return (
      <div>
        <label className="text-sm font-medium text-slate-700">{field.label}</label>
        <div className="mt-1.5">
          <FileUpload
            value={displayValue}
            onChange={onChange}
            accept="image/*"
            label="Upload File"
            testid={testid}
          />
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{field.label}</label>
      <input
        type={field.type || "text"}
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        className={base}
        placeholder={field.placeholder}
        data-testid={testid}
      />
    </div>
  );
}
